/**
 * Logout-warning preference (issue #201).
 *
 * `Employee.logout_warning_pref` reads as "show the warning", while `useSessionKeepalive`
 * takes the inverse (`disableWarning`). That inversion is converted here, in one place, so
 * the shell and the account page cannot drift apart on it.
 *
 * The preference is rendered into the page as `window.loggedInEmployee.logout_warning_pref`
 * (see `extend_context_for_user` in TECWeb/TECCore/view_master.py), so it is available
 * synchronously at first paint and needs no fetch.
 */

/** Dispatched on `window` after a Preferences save succeeds. */
export const LOGOUT_WARNING_PREF_EVENT = 'tec:logout-warning-pref-changed';

export interface LogoutWarningPrefDetail {
  /** The saved preference itself: `true` means "show the warning". */
  logoutWarningPref: boolean;
}

interface EmployeeLogoutWarningPref {
  logout_warning_pref?: unknown;
}

type WindowWithEmployee = Window & { loggedInEmployee?: EmployeeLogoutWarningPref | null };

/** Convert the saved preference into the `disableWarning` option the keepalive hook takes. */
export function toDisableWarning(logoutWarningPref: unknown): boolean {
  // Only an explicit `false` suppresses the warning. Anything else — missing, undefined, or a
  // non-boolean from an older payload — keeps it, which matches the model default of `True`.
  return logoutWarningPref === false;
}

/** Read the preference the server rendered into the page. */
export function readInitialDisableWarning(
  employee: EmployeeLogoutWarningPref | null | undefined = (window as WindowWithEmployee).loggedInEmployee,
): boolean {
  return toDisableWarning(employee?.logout_warning_pref);
}

/** Announce a newly saved preference so the shell applies it without a reload. */
export function publishLogoutWarningPref(logoutWarningPref: boolean): void {
  window.dispatchEvent(
    new CustomEvent<LogoutWarningPrefDetail>(LOGOUT_WARNING_PREF_EVENT, {
      detail: { logoutWarningPref },
    }),
  );
}
